import React, { Fragment, useContext, useState } from 'react';
import { Alert } from 'react-bootstrap';
import { TextField } from '@mui/material';
// contexts
import RunContext from '../../Contexts/RunContext.js';
import { UserContext } from "../../Contexts/UserContext/UserContext.js";
import TxStateContext from '../../Contexts/TxStateContext.js';
import SwapBotContext, { txstate_buytokens_oracle } from '../../Contexts/SwapBotContext.js';
// hooks
import useLog from '../../hooks/useLog.js';
// components
import { Row } from "../../components/Row";
import { Spaces } from "../../components/Spaces";
import SkeletonText from "../../components/SkeletonText";
import { Tableau } from '../../components/Tableau';
import SoldeBSV from '../../components/SoldeBSV.js';
import { TextDisplay, TextDisplayToken } from "../../components/TextDisplay/TextDisplay";
// helpers
import {
	ceil_to_2_decimals, forceFloat, partial_obj, sort_pendings_first,
	get_afterPercentFee_toAdd, getLog
} from "../../helpers/helpers.js"
import { translateErrorToJSXMessage } from '../../translateErrorToJSXMessage';
import { toFixedDown, sats_to_eur, sats_to_fiat } from "../../helpers/convert_bsv_tokens.js"


// Note about txstate_buytokens_oracle:
// For buying tokens, we use a custom txstate since we have 2 steps to interact with the oracle.
// Because of this, we will not use the useTxSender that only works with the default txstate

const _USDEUR = 0.88 // TODO convert to dynamic USD_EUR value in each function
const _PERCFEE = 1 // 1.5 for 1.5%


function get_total_price_EUR(function_id, swapbotContext, buy_nb_tokens, returnArray = false) {
	let log = getLog(function_id + "get_total_price_EUR(): ")

	const base_price = swapbotContext.base_price
	const secondary_price = swapbotContext.secondary_price
	const nb_available_tokens = swapbotContext.nb_available_tokens
	const buy_oracle_priceUSD = swapbotContext.buy_oracle_priceUSD

	// calculate total price

	const expected_nb_tokens_at_base_price = Math.min(buy_nb_tokens, nb_available_tokens)
	const expected_nb_tokens_at_secondary_price = buy_nb_tokens - expected_nb_tokens_at_base_price

	const tokens_price_base = ceil_to_2_decimals(expected_nb_tokens_at_base_price * base_price)
	const tokens_price_secondary = ceil_to_2_decimals(expected_nb_tokens_at_secondary_price * secondary_price)
	const tokens_price_EUR = ceil_to_2_decimals(tokens_price_base + tokens_price_secondary)

	const fees_USD_oracle = buy_oracle_priceUSD + 0.005 // add some average fee estimation
	const USD_EUR = _USDEUR // hardcoded value at 29.12.2021, only used for fees // TODO convert to dynamic USD_EUR value
	const basicFees_EUR_oracle = fees_USD_oracle * USD_EUR

	// since percent fees are percent AFTER adding the fees, we need this special formula
	// ex: 100+1% = 101, -1%=99.99 et pas 100 ! => il faut enfait 101.02 ! car 101.02-1%=100
	var afterPercFees_toAdd_EUR = get_afterPercentFee_toAdd(tokens_price_EUR, _PERCFEE)
	// var fees_EUR_perc_tokkenr = afterPercFees_toAdd_EUR * 1 / 3  // extract 1/3 of percent fees for 0.5% to tokkenr

	const fees_total_EUR = basicFees_EUR_oracle + afterPercFees_toAdd_EUR
	const total_price_EUR = tokens_price_EUR + fees_total_EUR

	log("\n total_price_EUR=", total_price_EUR,
		"\n tokens_price_EUR=", tokens_price_EUR,
		"\n fees_total_EUR=", fees_total_EUR,
		"\n basicFees_EUR_oracle=", basicFees_EUR_oracle,
		"\n afterPercFees_toAdd_EUR=", afterPercFees_toAdd_EUR,
	)

	if (!returnArray) return total_price_EUR
	else return [
		total_price_EUR, fees_total_EUR, tokens_price_base, tokens_price_secondary,
		expected_nb_tokens_at_base_price, expected_nb_tokens_at_secondary_price
	]
}



// if necessary we can make a UserBuyRequestsContext, ou un hook ? 
/**
 * generate buy_textDisplay_jsx => display of recap
 * 
 * props: 
 * 		- finished_loading		boolean
 * 		- buy_nb_tokens			int
 * 		- tokenName_plural		string
 * 		- delphetx_state		dict with "status", "send_retries", "error", "nb_tokens"fields
 * 		- get_total_price_EUR_callback		optional callback, will be called with total_price_EUR
 */
const RecapAchat = (props) => {
	const function_id = "RecapAchat: "
	// let log=useLog(function_id)

	const finished_loading = props.finished_loading
	var buy_nb_tokens = props.buy_nb_tokens
	var tokenName_plural = props.tokenName_plural
	const txstate = props.delphetx_state

	const swapbotContext = useContext(SwapBotContext)
	const [
		total_price_EUR, fees_total_EUR, tokens_price_base, tokens_price_secondary,
		expected_nb_tokens_at_base_price, expected_nb_tokens_at_secondary_price
	] = get_total_price_EUR(function_id, swapbotContext, buy_nb_tokens, true)

	var buy_textDisplay_jsx = null

	if (buy_nb_tokens) {
		if (!finished_loading) buy_textDisplay_jsx = <SkeletonText width="14em" height="1.5em" />
		else buy_textDisplay_jsx = <Fragment>
			Achat de {buy_nb_tokens} {tokenName_plural}: <br /><br />

			{expected_nb_tokens_at_base_price} {tokenName_plural} au prix de base soit {tokens_price_base}€ <br />
			{(expected_nb_tokens_at_secondary_price ? <Fragment>
				+ {expected_nb_tokens_at_secondary_price} {tokenName_plural} au prix secondaire soit {tokens_price_secondary}€ <br />
			</Fragment> : null)}
			+ frais (1% dont frais blockchain) soit {ceil_to_2_decimals(fees_total_EUR)}€ <br /><br />

			Prix total: {ceil_to_2_decimals(total_price_EUR)} €
		</Fragment>
	}

	if (txstate && txstate.status === txstate_buytokens_oracle.sending) {
		buy_textDisplay_jsx = <span>
			Envoi de la demande d'achat de {buy_nb_tokens} {tokenName_plural} en cours...
		</span>
	}
	else if (txstate && txstate.status === txstate_buytokens_oracle.sending_step2) {
		buy_textDisplay_jsx = "En attente de l'oracle..." + ('.'.repeat(txstate["send_retries"]))
	}
	else if (txstate && txstate.status === txstate_buytokens_oracle.sending_step2_toolong) {
		buy_textDisplay_jsx = "L'oracle met plus de temps que normal à répondre. "
			+ "Nous travaillons à rendre les achats plus rapides. "
			+ "En attente de l'oracle ..." + ('.'.repeat(txstate["send_retries"]))
	}
	else if (txstate && txstate.status === txstate_buytokens_oracle.sending_step3) {
		buy_textDisplay_jsx = "Oracle étape 1 réussie, envoi étape 2 en cours..."
	}
	else if (txstate && txstate.status === txstate_buytokens_oracle.sentsuccess) {
		buy_textDisplay_jsx = "✔️ Succès: demande d'achat de " + txstate.nb_tokens
			+ " " + tokenName_plural + " réussie."
	}
	else if (txstate && txstate.status === txstate_buytokens_oracle.sentfail) {
		buy_textDisplay_jsx = txstate["error"]
	}

	if (buy_textDisplay_jsx) {
		return (
			<Row>
				<TextDisplay>{buy_textDisplay_jsx}</TextDisplay>
			</Row>
		)
	}
	else return null
}



/**
 * generates values to display from raw array of user_sell_requests
 */
function generate_table_values_display(
	user_buy_requests, function_id, curr_bsveur, has_loaded, clicked_cancel_request
) {
	const log = getLog(function_id + "generate_table_values_display(): ")

	const table_values = []

	if (has_loaded && user_buy_requests && user_buy_requests.length > 0) {
		for (let i = 0; i < user_buy_requests.length; i++) {
			var user_request = user_buy_requests[i]

			var oracle_request = null, admin_request = null
			if (user_request.oracle_TokenBuyRequest_BasePrice) {
				oracle_request = user_request.oracle_TokenBuyRequest_BasePrice
				if (oracle_request.success_TokenBuyRequest_Admin) {
					admin_request = oracle_request.success_TokenBuyRequest_Admin
				}
			}

			// date

			var timestamp = user_request.get_timestamp()
			var date = "inconnue"
			if (!isNaN(timestamp)) {
				date = new Date(timestamp).toLocaleString("fr-FR", { hour12: false })
			}

			// total_EUR_paid

			var total_EUR_paid = 0
			try { // we need try{} because the oracle might throw if undefined
				// if(oracle_request && oracle_request.satoshis_buy_afterFeePercent && oracle_request.BSVEUR){
				// 	// calculate with the value spent after fees
				// 	total_EUR_paid = oracle_request.satoshis_buy_afterFeePercent * 1e-8 * oracle_request.BSVEUR 
				// }
				if (oracle_request && oracle_request.satoshis_buy_afterFeePercent
					&& oracle_request.paid_tokens_price_sats_afterPercentFee && oracle_request.BSVEUR_oracle
				) {
					total_EUR_paid = sats_to_fiat(
						oracle_request.paid_tokens_price_sats_afterPercentFee,
						oracle_request.BSVEUR_oracle
					)
				}
			} catch (e) { }

			if (total_EUR_paid === 0) {
				// if we can't get the price from the oracle, we get the current one
				// so the user can manually cancel
				// update: we don't put any money into init() anymore so user_cancel is useless?
				total_EUR_paid = user_request.paid_tokens_price_EUR * 1e-8 * curr_bsveur

				log("could not retrieve total_EUR_paid from user_request.get_total_EUR_paid():",
					"\n user_request=", user_request,
					"\n user_request.satoshis=", user_request.satoshis,
					"\n user_request.paid_tokens_price_EUR=", user_request.paid_tokens_price_EUR,
					"\n curr_bsveur=", curr_bsveur,
					"\n total_EUR_paid based on current rate:", total_EUR_paid)
			}

			// nb_tokens (total)

			// problem with nb_tokens_secondaryPrice => if base_price or secondary_price changes
			// the calcul will be wrong

			var nb_tokens_basePrice = user_request.expected_nb_tokens_base_price
			var nb_tokens_secondaryPrice = user_request.expected_nb_tokens_secondary_price
			// var paid_base_price_EUR = user_request.paid_base_price_EUR
			// var paid_secondary_price_EUR = user_request.paid_secondary_price_EUR

			var nb_tokens = nb_tokens_basePrice + nb_tokens_secondaryPrice

			log(
				"\n nb_tokens=", nb_tokens,
				"\n !!admin_request=", !!admin_request,
				"\n nb_tokens_basePrice=", nb_tokens_basePrice,
				"\n nb_tokens_secondaryPrice=", nb_tokens_secondaryPrice,
				"\n admin_request=", admin_request,
				"\n total_EUR_paid=", total_EUR_paid,
				// "\n (otherwise: base_price=",base_price,", secondary_price=",secondary_price,")",
			)

			// status

			// retrieve status
			var status = "error" // default
			try { // we need try{} because the oracle might throw if undefined
				status = user_request.get_status()
				log("user_request.get_status()=", status)
			} catch (e) { }
			// after redeploy, user_request.get_status() should automatically handle this
			if (admin_request) {
				status = admin_request.status
			}

			// get refund_txid & paid_txid if they exists
			var refund_txid = ""
			var paid_txid = ""
			if (oracle_request) {
				if (oracle_request.BSV_Refund) {
					//status = "refunded_by_oracle"
					refund_txid = oracle_request.BSV_Refund.location
				}
				else if (admin_request) {
					if (admin_request.BSV_Refund) {
						//status = "refunded_by_admin"
						refund_txid = admin_request.BSV_Refund.location
					}
					else if (admin_request.paid_tokens) {
						paid_txid = admin_request.paid_tokens.location
					}
				}
			}

			if (!refund_txid || refund_txid === "") {
				refund_txid = user_request.location // better than nothing
			}

			if (!paid_txid || paid_txid === "") {
				paid_txid = user_request.location // better than nothing
			}

			var refund_url = "https://whatsonchain.com/tx/" + refund_txid.split('_')[0]
			var paid_url = "https://whatsonchain.com/tx/" + paid_txid.split('_')[0]
			var tx_url = "https://whatsonchain.com/tx/" + user_request.location.split('_')[0]

			// cancel button

			const user_request_location = user_request.location // needed for react safety
			var button_cancel =
				<button className="button-main-style-red"
					onClick={() => { clicked_cancel_request(user_request_location) }}>
					Echec, cliquez pour récupérer BSV
				</button>



			// convert status to jsx
			var status_jsx = null
			if (status.includes("refunded")) {
				status_jsx =
					<a href={refund_url} target="_blank" rel="noopener noreferrer" style={{ color: "springgreen" }}>
						remboursé
					</a>
			}
			else if (status === "pending_oracle") {
				status_jsx =
					<a href={refund_url} target="_blank" rel="noopener noreferrer" style={{ color: "white" }}>
						échoué
					</a>
			}
			else if (status === "user_cancelled") {
				status_jsx =
					<a href={refund_url} target="_blank" rel="noopener noreferrer" style={{ color: "white" }}>
						annulé par vous
					</a>
			}
			else if (!user_request.oracle_TokenBuyRequest_BasePrice) {
				status_jsx = button_cancel
			}
			else if (status.includes("success")) {
				status_jsx =
					<a href={tx_url} target="_blank" rel="noopener noreferrer" style={{ color: "orange" }}>
						attente de paiement
					</a>
			}
			else if (status.includes("pending")) {
				status_jsx =
					<a href={tx_url} target="_blank" rel="noopener noreferrer" style={{ color: "orange" }}>
						attente de paiement
					</a>
			}
			else if (status.includes("paid")) {
				status_jsx =
					<a href={paid_url} target="_blank" rel="noopener noreferrer" style={{ color: "springgreen" }}>
						payé
					</a>
			} else {
				status_jsx = status
			}


			var data_for_table = {
				"date": date,
				"nb_tokens": parseInt(nb_tokens),
				"total_EUR_paid": total_EUR_paid.toFixed(2) + " €",
				"status_jsx": status_jsx,
				"status": status,
				// var oracle_bsveur = 0
				// if(user_request.oracle_BSVEUR)
				// 		oracle_bsveur = user_request.oracle_BSVEUR.value || 0 // can be undefined
				// 	// oracle_bsveur.toFixed(2) + " €", // the fixed bsv/eur from the oracle is of no value to users
				// 	// better put the link directly on the status
				// 	// <a href={tx_url} target="_blank">{location.slice(0,4)+".."+location.slice(-2)}</a>
			}

			table_values.push(data_for_table)
		}
	}

	// reorder table_values to make sure pendings are displayed first no matter the date
	sort_pendings_first(table_values)

	// format data to be ready to display
	const keep_keys = ["date", "nb_tokens", "total_EUR_paid", "status_jsx"]
	var table_values_display = []
	for (let i = 0; i < table_values.length; i++) {
		table_values_display.push(
			Object.values(partial_obj(table_values[i], keep_keys))
		)
	}

	return table_values_display
}




const _AchatsStatusHistory = (props) => {
	const function_id = "AchatsStatusHistory: "
	const classname = function_id
	let log = useLog(function_id)

	const tokenName = "Delphe"
	const tokenName_plural = tokenName + "s"

	const swapbotContext = useContext(SwapBotContext)
	const user_buy_requests = swapbotContext.user_buy_requests
	// const base_price = swapbotContext.base_price
	// const secondary_price = swapbotContext.secondary_price

	const userContext = useContext(UserContext)
	const curr_bsveur = userContext.bsvprice

	const runContext = useContext(RunContext)
	const run = runContext.run

	const txStateContext = useContext(TxStateContext)

	const [error_update_requests, set_error_update_requests] = useState(null)

	if (user_buy_requests && user_buy_requests.length > 0) {
		log("", user_buy_requests.length, "user_buy_requests=", user_buy_requests)
	} else {
		log("0 user_buy_requests")
	}

	async function clicked_cancel_request(userRequest_txid) {
		const function_id = classname + "clicked_cancel_request(): userRequest_txid=" + userRequest_txid + ": "
		const log = getLog(function_id)

		if (!run) return

		log("called")
		alert("Cliquez sur OK pour récupérer les BSV")

		await txStateContext.take_broadcast_lock()

		run.activate()
		var userRequest = await run.load(userRequest_txid)
		await userRequest.sync()
		log("called: userRequest=", userRequest)

		try {
			log("calling userRequest.user_cancel()...")
			userRequest.user_cancel()
			await userRequest.sync()
			log("SUCCESS for userRequest.user_cancel(): userRequest=", userRequest)
			alert("Annulation réussie, vos BSV ont été remboursés.")
		}
		catch (error) {
			var error_jsx = translateErrorToJSXMessage(error)
			console.error(function_id + error)
			set_error_update_requests(error_jsx)
		}
		await txStateContext.release_broadcast_lock()
		await swapbotContext.refresh_user_buy_requests()
	}

	const has_loaded = (user_buy_requests != null)

	let table_values_display = []
	// try&catch to avoid black screen if an error occurs
	try {
		table_values_display = generate_table_values_display(
			user_buy_requests, function_id, curr_bsveur, has_loaded, clicked_cancel_request
		)
	} catch (error) {
		let premess = "Error in " + function_id
		this.globalErrorContext.throwContextError(error, premess)
	}

	return (
		<Fragment>
			<h1><i className="fas fa-tasks"></i><Spaces count="3" />
				Status des achats de {tokenName_plural}
			</h1>

			{error_update_requests ?
				error_update_requests
				: null}

			<Alert variant="light" style={{ margin: "20px 40px" }}>
				<i className="fa fa-info-circle" style={{ fontSize: "25px", marginRight: "15px" }} />
				Cliquez sur le status pour voir la transaction associée sur un explorateur blockchain.
			</Alert>

			<Tableau
				titles={["Date", tokenName_plural + " achetés", "Prix", "Status"]}
				values={table_values_display}
				loading={!has_loaded}
				empty_mess="Aucune demande effectuée pour le moment."
			/>
			<br /><br />

		</Fragment>
	)
}
export const AchatsStatusHistory = React.memo(_AchatsStatusHistory)



export function Slide_EchangeTokens_Achat(props) {
	const function_id = "Slide_EchangeTokens_Achat: "
	// const classname = function_id
	let log = useLog(function_id)

	const tokenName = "Delphe"
	const tokenName_plural = tokenName + 's'

	// swapbotContext
	const swapbotContext = useContext(SwapBotContext)
	const base_price = swapbotContext.base_price
	const secondary_price = swapbotContext.secondary_price
	const nb_available_tokens = swapbotContext.nb_available_tokens
	const isWrongTokenType_buy = swapbotContext.isWrongTokenType_buy

	// states
	const [buy_nb_tokens, set_buy_nb_tokens] = useState(null)

	// update delphetx_state based on txStateContext
	const txStateContext = useContext(TxStateContext)

	// userContext
	const userContext = useContext(UserContext)
	const tokenID = userContext.get_selected_coin_props().tokenid
	const tokenActionID = tokenID + "_buy" // or buy_delphe ?

	const balancebsv = userContext.balance_bsv
	const bsvprice = userContext.bsvprice
	const bsveur = bsvprice
	var soldeeur = null
	if (balancebsv && bsvprice) {
		soldeeur = toFixedDown(sats_to_eur(balancebsv, bsveur), 2)
	}

	const delphetx_state = txStateContext.sendtxs_state ? txStateContext.sendtxs_state[tokenActionID] : null

	const finished_loading = Boolean(
		base_price != null && secondary_price != null && nb_available_tokens != null
		&& balancebsv != null && bsvprice != null
	)

	log(" txStateContext.sendtxs_state=", txStateContext.sendtxs_state,
		"\n tokenActionID=", tokenActionID,
		"\n delphetx_state=", delphetx_state,
		"\n txStateContext.broadcast_lock=", txStateContext.broadcast_lock)

	// --- FUNCTIONS ---

	const func_empty_fields = () => {
		set_buy_nb_tokens("")
	}

	const [local_txstate_timestamp, set_local_txstate_timestamp] = useState(0)
	const func_resetMess = () => {
		if (local_txstate_timestamp) {
			// if we are already displaying sentfail or sentsuccess message,
			// we increase local timestamp to stop displaying it and move on
			set_local_txstate_timestamp(local_txstate_timestamp + 1) // Date.now() also works
		}
	}

	function handleChange_buy_nb_tokens(event) {
		if (txStateContext.broadcast_lock) return
		func_resetMess()

		var val = event.target.value
		// beware we want to allow '0.'
		if (val !== '0.') {
			val = forceFloat(val)
			if (Number.isNaN(val)) val = ""
		}
		if (val < 0) val = -1 * val
		log("new token_balance = ", val)

		set_buy_nb_tokens(val)
	}

	function acheterTokens(event) {
		event.preventDefault() // prevent redirection
		swapbotContext.buyTokens(tokenActionID, buy_nb_tokens, tokenName)
	}

	let total_price_EUR = ceil_to_2_decimals(get_total_price_EUR(function_id, swapbotContext, buy_nb_tokens))
	if (!total_price_EUR) total_price_EUR = 0
	var expected_remaining_bsv_in_eur = 0
	// calculate how much BSV (in eur) will remain on the wallet if buy is made
	if (soldeeur) {
		let new_expected_remaining_bsv_in_eur = parseFloat(soldeeur) - total_price_EUR
		log("get_total_price_EUR_callback(): new_expected_remaining_bsv_in_eur=", new_expected_remaining_bsv_in_eur)
		expected_remaining_bsv_in_eur = new_expected_remaining_bsv_in_eur
	}

	const has_error_remaining_bsv = Boolean(
		expected_remaining_bsv_in_eur && expected_remaining_bsv_in_eur < 1.00
	)

	var buy_button_style = {}
	if (
		!finished_loading || txStateContext.broadcast_lock || has_error_remaining_bsv
		|| !buy_nb_tokens || buy_nb_tokens <= 0
	) {
		buy_button_style = {
			backgroundColor: "gray",
			pointerEvents: "none",
		}
	}

	let maxInput_isReady = Boolean(
		soldeeur != null && base_price != null && secondary_price != null && nb_available_tokens != null
		&& soldeeur > 1
	)

	function handleClick_maxInput() { // in tokens
		log = (...args) => { console.log(function_id + "handleClick_maxInput:", ...args) }
		let eur_to_spend = soldeeur - 1  // keep 1€ at least 

		let total_fees = ceil_to_2_decimals(swapbotContext.buy_oracle_priceUSD + 0.005) * _USDEUR
			+ get_afterPercentFee_toAdd(eur_to_spend, _PERCFEE)
		// remove fees from spendable total to no trigger error "must keep at east 1€ of bsv"
		eur_to_spend -= total_fees

		// compute corresponding amount of tokens
		let wanted_nbtokens_base_price = parseInt(eur_to_spend / base_price)
		let wanted_nbtokens_secondary_price = 0
		if (wanted_nbtokens_base_price > nb_available_tokens) {
			wanted_nbtokens_base_price = nb_available_tokens
			let remaining_eur_for_secondary_price = eur_to_spend - wanted_nbtokens_base_price * base_price
			wanted_nbtokens_secondary_price = parseInt(remaining_eur_for_secondary_price / secondary_price)
		}
		let total_tokens_wanted = wanted_nbtokens_base_price + wanted_nbtokens_secondary_price

		log("\n total_tokens_wanted=", total_tokens_wanted,
			"\n soldeeur=", soldeeur,
			"\n total_fees=", total_fees,
			"\n eur_to_spend=", eur_to_spend,
			"\n wanted_nbtokens_base_price=", wanted_nbtokens_base_price,
			"\n wanted_nbtokens_secondary_price=", wanted_nbtokens_secondary_price,
			"\n base_price=", base_price,
			"\n secondary_price=", secondary_price,
		)

		// call handleChange with fake event
		let fake_event = { target: {} }
		fake_event.target.value = total_tokens_wanted
		handleChange_buy_nb_tokens(fake_event)
	}

	if (delphetx_state && delphetx_state.status === txstate_buytokens_oracle.sentsuccess) {
		if (local_txstate_timestamp < delphetx_state.timestamp) { // only once
			set_local_txstate_timestamp(delphetx_state.timestamp)
			func_empty_fields()
		}
	}
	else if (delphetx_state && delphetx_state.status === txstate_buytokens_oracle.sentfail) {
		if (local_txstate_timestamp < delphetx_state.timestamp) { // only once
			set_local_txstate_timestamp(delphetx_state.timestamp)
		}
	}

	if (isWrongTokenType_buy) {
		return <div>
			Achats non disponibles pour ce type de token
		</div>
	}


	return (
		<div>
			<h1><i className="fas fa-shopping-cart" /><Spaces count="3" />
				Achat de {tokenName_plural}
			</h1>
			<br />

			<Alert variant="light">
				<i style={{ fontSize: "25px", marginRight: "10px" }} className="fa fa-info-circle" />
				Les achats de {tokenName_plural} sont envoyés manuellement le 1er du mois suivant,
				juste après la publication du mintuple.
			</Alert><br />

			<TextDisplayToken>
				{/* beware you can't do just {myvar?myvar:<SkeletonText/>} because myvar=0 is a valid value */}
				• &nbsp; <b>
					{nb_available_tokens != null ? nb_available_tokens : <SkeletonText width="3em" />}
				</b>&nbsp; Delphes
				disponibles à &nbsp;<b>
					{base_price != null ? base_price : <SkeletonText width="3em" />}
				</b> € (prix de base) <br />
				<br />

				• &nbsp; Une fois épuisés, le prix passe à &nbsp;<b>
					{secondary_price != null ? secondary_price : <SkeletonText width="3em" />}
				</b>
				&nbsp;€ (prix secondaire) <br /><br />

				<Alert variant="warning" style={{ margin: "0" }}>
					<i style={{ fontSize: "25px", marginRight: "10px" }} className="fa fa-info-circle" />
					Il y a un nombre limité de {tokenName_plural} au prix de base chaque mois.
					Une fois épuisés, vous pouvez attendre le mois suivant
					ou acheter tout de suite des {tokenName_plural} au prix secondaire.
				</Alert>
			</TextDisplayToken>

			<SoldeBSV />
			<br />

			{/* <RowWithTitle
				title={<Fragment>
					Nombre de {tokenName_plural} à acheter
				</Fragment>}
			>
				<input
					type='text'
					placeholder="ex: 300"
					value={buy_nb_tokens?buy_nb_tokens:""}
					onChange={handleChange_buy_nb_tokens}
					id='update_buy_nb_tokens'
					className='item'
				/>
				{expected_remaining_bsv_in_eur && expected_remaining_bsv_in_eur<1.00?
					<span style={{color:"red"}}>
						vous devez toujours garder au moins 1€ de BSV sur votre wallet pour les transactions futures
					</span>
				:null}
			</RowWithTitle> */}

			<Row>
				<TextField fullWidth
					id="outlined-basic" variant="outlined"
					label={<span>Amount of {tokenName_plural} to mint</span>}
					value={buy_nb_tokens ? buy_nb_tokens : ""}
					onChange={handleChange_buy_nb_tokens}
					placeholder="ex: 300.50"
					error={has_error_remaining_bsv}
					// helperText={has_error_remaining_bsv?
					// 	"vous devez toujours garder au moins 1€ de BSV sur votre wallet pour les transactions futures"
					// :null}
					helperText={soldeeur && soldeeur > 1 ?
						"vous devez toujours garder au moins 1€ de BSV sur votre wallet pour les transactions futures"
						: "vous devez avoir plus de 1€ de BSV pour faire des achats"
					}
					InputProps={maxInput_isReady && soldeeur > 1 ? {
						endAdornment:
							<span style={{ cursor: "pointer", color: "lightgrey" }}
								onClick={handleClick_maxInput}>
								MAX
							</span>
					} : {}}
				/>
			</Row><br />

			{/* if the txstate is too old, don't display anything */}
			{delphetx_state && local_txstate_timestamp > delphetx_state.timestamp ?
				null
				:
				<RecapAchat
					finished_loading={finished_loading}
					buy_nb_tokens={buy_nb_tokens}
					tokenName_plural={tokenName_plural}
					delphetx_state={delphetx_state}
				/>
			}

			<Row>
				<button className="submitbutton" onClick={acheterTokens} style={buy_button_style}>
					Acheter {buy_nb_tokens} {tokenName_plural}
					{finished_loading && buy_nb_tokens && buy_nb_tokens > 0 ?
						" pour " + total_price_EUR + " €"
						: ""}
				</button>
			</Row>

			<br /><br /><br /><br />

			<AchatsStatusHistory />

		</div>
	)
}
